import { ArrowRight, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { localizePath, type LocaleCode, useI18n } from "@/i18n";
import { serviceAreas } from "@/config/service-areas";
import { heroVideo } from "@/config/images";
import { localizeAreaName } from "@/i18n/area-names";
import { trackAnalyticsEvent } from "@/lib/analytics";

type HeroCopy = {
  eyebrow: string;
  title: string;
  body: string;
  estimate: string;
  urgent: string;
  emergencyLabel: string;
  emergencyArea: string;
  scheduledLabel: string;
  scheduledArea: string;
  mobilization: string;
  callLabel: string;
};

const HERO_COPY: Record<LocaleCode, HeroCopy> = {
  "en-ca": {
    eyebrow: "Lower Mainland response · Province-wide planned projects",
    title: "Roofing Built for BC",
    body: "Emergency and urgent roofing across the Lower Mainland, subject to crew availability. Scheduled residential, commercial, strata and specialty roofing projects throughout British Columbia.",
    estimate: "Get a Free Roofing Estimate",
    urgent: "Roof Leak? Get Urgent Help",
    emergencyLabel: "Emergency & Urgent",
    emergencyArea: "Lower Mainland",
    scheduledLabel: "Scheduled Projects",
    scheduledArea: "British Columbia",
    mobilization:
      "Mobilization and minimum project requirements may apply to projects outside the Lower Mainland.",
    callLabel: "Call Revolin Roofing",
  },
  "zh-cn": {
    eyebrow: "低陆平原紧急响应 · 全省计划工程",
    title: "为卑诗省而建的屋顶",
    body: "我们在施工团队有空档的情况下，为低陆平原各地提供紧急和急需屋顶服务；并在卑诗省全境承接预先安排的住宅、商业、共管物业及专业屋顶工程。",
    estimate: "获取免费屋顶估价",
    urgent: "屋顶漏水？获取紧急帮助",
    emergencyLabel: "紧急与急需服务",
    emergencyArea: "低陆平原",
    scheduledLabel: "计划工程",
    scheduledArea: "卑诗省",
    mobilization: "低陆平原以外的工程可能适用动员费用及最低工程要求。",
    callLabel: "致电 Revolin Roofing",
  },
  "zh-tw": {
    eyebrow: "低陸平原緊急支援 · 全省預約工程",
    title: "為卑詩省而建的屋頂",
    body: "我們視施工團隊檔期，在低陸平原各地提供緊急及急需屋頂服務；並於卑詩省全境承接預先安排的住宅、商業、共管物業及專業屋頂工程。",
    estimate: "取得免費屋頂估價",
    urgent: "屋頂漏水？取得緊急協助",
    emergencyLabel: "緊急與急需服務",
    emergencyArea: "低陸平原",
    scheduledLabel: "預約工程",
    scheduledArea: "卑詩省",
    mobilization: "低陸平原以外的工程可能適用動員費用及最低工程要求。",
    callLabel: "致電 Revolin Roofing",
  },
  "pa-in": {
    eyebrow: "ਲੋਅਰ ਮੇਨਲੈਂਡ ਐਮਰਜੈਂਸੀ ਸੇਵਾ · ਪੂਰੇ ਸੂਬੇ ਵਿੱਚ ਯੋਜਨਾਬੱਧ ਪ੍ਰੋਜੈਕਟ",
    title: "ਬੀਸੀ ਲਈ ਬਣੀ ਛੱਤ",
    body: "ਕਰੂ ਦੀ ਉਪਲਬਧਤਾ ਅਨੁਸਾਰ ਲੋਅਰ ਮੇਨਲੈਂਡ ਵਿੱਚ ਐਮਰਜੈਂਸੀ ਅਤੇ ਫੌਰੀ ਛੱਤ ਸੇਵਾ। ਪੂਰੇ ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ ਵਿੱਚ ਯੋਜਨਾਬੱਧ ਰਿਹਾਇਸ਼ੀ, ਵਪਾਰਕ, ਸਟ੍ਰਾਟਾ ਅਤੇ ਵਿਸ਼ੇਸ਼ ਛੱਤ ਪ੍ਰੋਜੈਕਟ।",
    estimate: "ਮੁਫ਼ਤ ਛੱਤ ਅੰਦਾਜ਼ਾ ਲਵੋ",
    urgent: "ਛੱਤ ਲੀਕ ਹੈ? ਫੌਰੀ ਮਦਦ ਲਵੋ",
    emergencyLabel: "ਐਮਰਜੈਂਸੀ ਅਤੇ ਫੌਰੀ",
    emergencyArea: "ਲੋਅਰ ਮੇਨਲੈਂਡ",
    scheduledLabel: "ਯੋਜਨਾਬੱਧ ਪ੍ਰੋਜੈਕਟ",
    scheduledArea: "ਬ੍ਰਿਟਿਸ਼ ਕੋਲੰਬੀਆ",
    mobilization:
      "ਲੋਅਰ ਮੇਨਲੈਂਡ ਤੋਂ ਬਾਹਰ ਦੇ ਪ੍ਰੋਜੈਕਟਾਂ ਲਈ ਮੋਬਿਲਾਈਜ਼ੇਸ਼ਨ ਅਤੇ ਘੱਟੋ-ਘੱਟ ਪ੍ਰੋਜੈਕਟ ਲੋੜਾਂ ਲਾਗੂ ਹੋ ਸਕਦੀਆਂ ਹਨ।",
    callLabel: "Revolin Roofing ਨੂੰ ਕਾਲ ਕਰੋ",
  },
  "ko-kr": {
    eyebrow: "로어 메인랜드 긴급 대응 · BC 전역 계획 공사",
    title: "BC 환경에 맞춘 지붕",
    body: "작업팀 일정에 따라 로어 메인랜드 전역에서 긴급 지붕 서비스를 제공합니다. 브리티시컬럼비아 전역에서는 주택, 상업용, 공동주택 및 특수 지붕 공사를 예약제로 진행합니다.",
    estimate: "무료 지붕 견적 받기",
    urgent: "지붕 누수인가요? 긴급 도움 받기",
    emergencyLabel: "긴급 서비스",
    emergencyArea: "로어 메인랜드",
    scheduledLabel: "예약 공사",
    scheduledArea: "브리티시컬럼비아",
    mobilization: "로어 메인랜드 외 지역은 출동 준비 비용과 최소 공사 요건이 적용될 수 있습니다.",
    callLabel: "Revolin Roofing에 전화",
  },
  "fa-ir": {
    eyebrow: "خدمات فوری در لوئر مین‌لند · پروژه‌های برنامه‌ریزی‌شده در سراسر استان",
    title: "سقف‌سازی متناسب با اقلیم بریتیش کلمبیا",
    body: "خدمات اضطراری و فوری سقف در سراسر لوئر مین‌لند، متناسب با ظرفیت تیم اجرایی. اجرای پروژه‌های برنامه‌ریزی‌شدهٔ مسکونی، تجاری، مجتمع‌های مسکونی و سیستم‌های تخصصی سقف در سراسر بریتیش کلمبیا.",
    estimate: "دریافت برآورد رایگان سقف",
    urgent: "سقف نشتی دارد؟ کمک فوری بگیرید",
    emergencyLabel: "خدمات اضطراری و فوری",
    emergencyArea: "لوئر مین‌لند",
    scheduledLabel: "پروژه‌های برنامه‌ریزی‌شده",
    scheduledArea: "بریتیش کلمبیا",
    mobilization:
      "برای پروژه‌های خارج از لوئر مین‌لند ممکن است هزینهٔ اعزام و حداقل شرایط اجرایی در نظر گرفته شود.",
    callLabel: "تماس با Revolin Roofing",
  },
  "ar-sa": {
    eyebrow: "استجابة عاجلة في لوَر مينلاند · مشاريع مخططة في أنحاء المقاطعة",
    title: "أسقف مصممة لمناخ بريتيش كولومبيا",
    body: "خدمات طارئة وعاجلة للأسقف في أنحاء لوَر مينلاند، بحسب توافر فرق العمل. وتنفيذ مشاريع الأسقف السكنية والتجارية والمجمعات السكنية والأنظمة المتخصصة المجدولة في أنحاء بريتيش كولومبيا.",
    estimate: "احصل على تقدير مجاني للسقف",
    urgent: "هل يتسرب سقفك؟ اطلب مساعدة عاجلة",
    emergencyLabel: "الخدمات الطارئة والعاجلة",
    emergencyArea: "لوَر مينلاند",
    scheduledLabel: "المشاريع المجدولة",
    scheduledArea: "بريتيش كولومبيا",
    mobilization: "قد تطبق متطلبات التنقل والحد الأدنى لحجم المشروع خارج منطقة لوَر مينلاند.",
    callLabel: "اتصل بـ Revolin Roofing",
  },
};

export function Hero() {
  const { t, locale } = useI18n();
  const copy = HERO_COPY[locale];

  return (
    <section id="top" className="relative isolate overflow-hidden bg-slate-deep">
      <video
        className="absolute inset-0 -z-10 size-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={heroVideo.poster}
        width={heroVideo.width}
        height={heroVideo.height}
        aria-label={locale === "fa-ir" || locale === "ar-sa" ? t.hero.imageCaption : heroVideo.alt}
      >
        <source src={heroVideo.src} type="video/mp4" />
      </video>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[linear-gradient(103deg,oklch(0.14_0.016_248/0.97)_0%,oklch(0.15_0.016_248/0.9)_45%,oklch(0.15_0.016_248/0.4)_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_16%_24%,oklch(0.75_0.12_75/0.12),transparent_34%)]"
      />

      <div className="container-page pb-16 pt-32 md:pb-24 md:pt-44">
        <div className="max-w-3xl">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1 className="display-1 mt-5 max-w-3xl text-balance text-primary-foreground">
            {copy.title}
          </h1>
          <p className="mt-6 max-w-2xl text-[1.05rem] leading-relaxed text-primary-foreground/78 md:text-lg">
            {copy.body}
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button variant="accent" size="xl" asChild>
              <a
                href={localizePath("/roof-estimate", locale)}
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "estimate_cta_click",
                    cta_location: "hero_estimate",
                    lead_type: "planned",
                  })
                }
              >
                {copy.estimate}
                <ArrowRight aria-hidden="true" />
              </a>
            </Button>
            <Button variant="outlineDark" size="xl" asChild>
              <a
                href={localizePath("/emergency-roofing", locale)}
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "emergency_cta_click",
                    cta_location: "hero_urgent",
                    lead_type: "emergency",
                  })
                }
              >
                <Siren aria-hidden="true" />
                {copy.urgent}
              </a>
            </Button>
          </div>

          <p className="mt-8 text-xs tracking-[0.14em] uppercase text-primary-foreground/50">
            {serviceAreas
              .slice(0, 6)
              .map((area) => localizeAreaName(area.slug, area.name, locale))
              .join(" · ")}
          </p>
        </div>
      </div>
    </section>
  );
}
